(function () {
  const section = document.querySelector('.vc-campaign-biomechanics');
  if (!section) return;

  const leftCol   = section.querySelector('.vc-campaign-biomechanics__left');
  const rightCol  = section.querySelector('.vc-campaign-biomechanics__right');
  const listItems = Array.from(section.querySelectorAll('.vc-campaign-biomechanics__list-item'));
  const steps     = Array.from(section.querySelectorAll('.vc-campaign-biomechanics__step'));

  if (!leftCol || !rightCol || !steps.length) return;

  const EASE     = 'cubic-bezier(0.7, 0.1, 0.3, 1)';
  const DUR_BASE = 0.35;

  /* ── Build per-step callout timelines (paused) ── */
  const stepTimelines = steps.map((step) => {
    const node      = step.querySelector('.vc-campaign-biomechanics__callout-node');
    const connector = step.querySelector('.vc-campaign-biomechanics__callout-connector path');
    const text      = step.querySelector('.vc-campaign-biomechanics__callout-text');

    const tl = gsap.timeline({ paused: true });

    if (node) {
      tl.fromTo(node,
        { scale: 0 },
        { scale: 1, duration: 0.3, ease: 'back.out(2)' }
      );
    }

    if (connector) {
      tl.fromTo(connector,
        { drawSVG: '0%' },
        { drawSVG: '100%', duration: 0.5, ease: EASE },
        node ? '-=0.1' : 0
      );
    }

    if (text) {
      tl.fromTo(text,
        { opacity: 0, y: 6 },
        { opacity: 1, y: 0, duration: DUR_BASE, ease: EASE },
        '-=0.2'
      );
    }

    return tl;
  });

  /* ── Set active list item ── */
  function setActive(index) {
    listItems.forEach((item, i) => {
      if (i === index) item.setAttribute('data-active', '');
      else item.removeAttribute('data-active');
    });
  }

  /* ── Scroll to step: step top lands at header bottom ── */
  function scrollToStep(step) {
    const smoother = typeof ScrollSmoother !== 'undefined' && ScrollSmoother.get();
    const hh = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    ) || 48;
    if (smoother) {
      smoother.scrollTo(step, true, `top top+=${hh}`);
    } else {
      const targetY = step.getBoundingClientRect().top + window.scrollY - hh;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }
  }

  /* ── Click / keyboard navigation on list items ── */
  listItems.forEach((item) => {
    const idx    = parseInt(item.dataset.stepIndex, 10);
    const target = steps[idx];
    if (!target) return;

    item.addEventListener('click', () => scrollToStep(target));
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        scrollToStep(target);
      }
    });
  });

  /* ── Heading and nav heights → CSS vars ──
     The mobile illustrations start right under the heading, whose height changes with
     copy and viewport width. Observed on the element itself, so it keeps working while
     it moves between the left column and the fixed dock. */
  const headingBlock = leftCol.querySelector('.vc-campaign-biomechanics__text');
  const navBlock     = leftCol.querySelector('.vc-campaign-biomechanics__list');
  if (headingBlock && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      section.style.setProperty('--_text-h', `${headingBlock.offsetHeight}px`);
    }).observe(headingBlock);
  }

  /* Same for the nav: the last illustration only needs to clear it, not a fixed share
     of the screen, so the empty run-out after the final step stays small. */
  if (navBlock && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      section.style.setProperty('--_nav-h', `${navBlock.offsetHeight}px`);
    }).observe(navBlock);
  }

  /* ── Init after ScrollSmoother ready ── */
  function init() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    const isMobile = window.matchMedia('(max-width: 675px)').matches;
    const headerH  = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    ) || 48;

    const textEl = leftCol.querySelector('.vc-campaign-biomechanics__text');
    const listEl = leftCol.querySelector('.vc-campaign-biomechanics__list');

    if (isMobile && textEl && listEl) {
      /* Mobile: heading and nav live in a fixed layer appended to <body>. They cannot
         be pinned with a transform: ScrollSmoother's transformed content lags the
         native scroll while iOS browser toolbars slide, which left a gap under the nav.
         position:fixed with bottom:0 follows the toolbars natively. Outside the pinned
         range the nodes sit back in the left column (top before, bottom after). */
      const dock = document.createElement('div');
      dock.className = 'vc-campaign-biomechanics__dock';
      dock.dataset.theme = section.dataset.theme || '';
      if (section.dataset.mobileNav) dock.dataset.mobileNav = section.dataset.mobileNav;
      document.body.appendChild(dock);

      const attach = (host) => host.append(textEl, listEl);

      ScrollTrigger.create({
        trigger: leftCol,
        start: `top top+=${headerH}`,
        endTrigger: rightCol,
        end: 'bottom bottom',
        onEnter()     { attach(dock); dock.dataset.active = ''; leftCol.removeAttribute('data-end'); },
        onEnterBack() { attach(dock); dock.dataset.active = ''; leftCol.removeAttribute('data-end'); },
        onLeave()     { attach(leftCol); delete dock.dataset.active; leftCol.setAttribute('data-end', ''); },
        onLeaveBack() { attach(leftCol); delete dock.dataset.active; leftCol.removeAttribute('data-end'); },
      });
    } else {
      /* Desktop: pin the left column while the right column scrolls.
         CSS position:sticky fails inside ScrollSmoother (overflow:hidden wrapper).
         pinSpacing:false on both: ScrollSmoother keeps pinned elements in flow via
         transforms (not position:fixed), so a spacer would create an unwanted gap. */
      ScrollTrigger.create({
        trigger: leftCol,
        start: `top top+=${headerH}`,
        endTrigger: rightCol,
        end: 'bottom bottom',
        pin: leftCol,
        pinSpacing: false,
      });
    }

    /* Compact mobile nav: one shared bar across the row that restarts every step */
    const sharedBar = section.querySelector('.vc-campaign-biomechanics__list-bar');
    const useSharedBar = isMobile && section.dataset.mobileNav === 'compact' && sharedBar;

    /* Per-step: activate item + play callout + scrub progress bar */
    steps.forEach((step, i) => {
      const tl          = stepTimelines[i];
      const progressBar = listItems[i]?.querySelector('.vc-campaign-biomechanics__list-progress');

      /* Center of the visible area below the sticky header */
      const midOffset = () => headerH + Math.round((window.innerHeight - headerH) / 2);

      ScrollTrigger.create({
        trigger: step,
        start: () => `top top+=${midOffset()}`,
        end:   () => `bottom top+=${midOffset()}`,
        onEnter()     { setActive(i); tl.play(); },
        onEnterBack() { setActive(i); tl.play(); },
        onLeave()     { /* keep active until next step takes over */ },
        onLeaveBack() {
          tl.reverse();
          if (i > 0) setActive(i - 1);
          else listItems.forEach(item => item.removeAttribute('data-active'));
        },
      });

      if (useSharedBar) {
        ScrollTrigger.create({
          trigger: step,
          start: () => `top top+=${midOffset()}`,
          end:   () => `bottom top+=${midOffset()}`,
          onEnter()     { sharedBar.style.setProperty('--_p', 0); },
          onEnterBack() { sharedBar.style.setProperty('--_p', 1); },
          onUpdate(self) { sharedBar.style.setProperty('--_p', self.progress); },
          onLeaveBack() { sharedBar.style.setProperty('--_p', 0); },
        });
      } else if (progressBar) {
        /* Scrubbed progress bar fills as user scrolls through this step */
        gsap.fromTo(progressBar,
          { width: '0%' },
          {
            width: '100%',
            ease: 'none',
            scrollTrigger: {
              trigger: step,
              start: () => `top top+=${midOffset()}`,
              end:   () => `bottom top+=${midOffset()}`,
              scrub: true,
            },
          }
        );
      }
    });

    /* Activate first item by default */
    if (listItems.length) setActive(0);
  }

  window.addEventListener('vc:scroll-ready', init, { once: true });

  if (document.readyState === 'complete') {
    setTimeout(init, 100);
  }
})();
